import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ContentHeader from "../../components/app/headers/ContentHeader";
import ItemList from "../../components/app/ItemList";
import PlaylistCard from "../../components/cards/PlaylistCard";
import Loader from "../../components/Loader";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventSelectItemPlaylist } from "../../tools/analytics/domains/playlist.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useApiQuery } from "../../services/api";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";

// `userId` names whose saved playlists to list: null for the reader's own.
export default function ProfileBookmarkedPlaylistsListPage({
  userId,
}: {
  userId: string | null;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(null);
  const { currentUser } = useCurrentUser();
  const scrollRef = useRef<HTMLDivElement>(null);
  const targetId =
    userId !== null ? userId : currentUser === null ? null : currentUser.id;

  const savedPlaylistsQuery = useApiQuery(
    "/webapp/private/users/:id/saved-playlists",
    targetId === null ? false : { param: { id: targetId } }
  );
  const displayedPlaylists =
    savedPlaylistsQuery.state === "success" ? savedPlaylistsQuery.body : [];
  const isLoading = savedPlaylistsQuery.state === "pending";
  const errorMessage =
    savedPlaylistsQuery.state === "error" ? savedPlaylistsQuery.message : null;
  const { savePageScrollY, restorePageScrollY } = usePageScroll(scrollRef);

  useEffectOnce(() => {
    if (displayedPlaylists.length > 0) {
      restorePageScrollY();
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      background="#f7f8fc"
      headerClassName="bg-white/90 backdrop-blur-md"
      header={<ContentHeader transparent />}
    >
      {errorMessage !== null ? (
        <p className="px-5 py-6 text-ui-body text-ds-slate-600">
          {errorMessage}
        </p>
      ) : isLoading || currentUser === null ? (
        <Loader />
      ) : (
        <ItemList className="pt-[16px]!">
          {displayedPlaylists.length > 0 ? (
            displayedPlaylists.map((playlist) => (
              <PlaylistCard
                key={playlist.id}
                playlist={playlist}
                variant="light"
                onClick={() => {
                  savePageScrollY();
                  gaEventSelectItemPlaylist({
                    playlist,
                    listName: AnalyticsItemList.PROFILE,
                  });
                  history.push({
                    pathname: publicPlaylistLink(playlist),
                    state: { playlist },
                  });
                }}
              />
            ))
          ) : (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <p>{t("No bookmarked playlists")}</p>
            </div>
          )}
        </ItemList>
      )}
    </ScrollableLayout>
  );
}
